import { Storage } from 'components/CommonFunc/amplifyStorageCompat';
import { API } from 'components/CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import React from "react";
import {ApiCallStatus} from "../../CommonFunc/Enum";
import {SearchResultTableViewOnly} from "../Common/SearchResultTableViewOnly";
import {DATA_HUB_DATA_API_BUCKET_NAME, dataHubApiConsts} from "./dataHubApiConsts";
import {ColumnMetadata} from "../Common/ColumnMetadata";

const logger = new ConsoleLogger("DataHubSearchResultTable.tsx")

interface DataHubSearchResultTableState {
    tableData: Object[]
    tableDataStatus: ApiCallStatus
    tableDataError: string

    tableDefinition: string
    tableDefinitionStatus: ApiCallStatus
    tableDefinitionError: string

    columnMetadata: ColumnMetadata[]
    columnMetadataStatus: ApiCallStatus
    columnMetadataError: string
}

interface DataHubSearchResultTableProps {
    refreshToken: string
    tableName: string
    tableTitle: string
    keywords: string
    largeTable: boolean
    comparator: string
}

export class DataHubSearchResultTable extends React.Component<DataHubSearchResultTableProps, DataHubSearchResultTableState> {
    static defaultProps = {
        largeTable: false
    }

    constructor(props: DataHubSearchResultTableProps) {
        super(props);
        this.state = {
            tableDefinition: "",
            tableDefinitionStatus: ApiCallStatus.NoData,
            tableDefinitionError: "",

            columnMetadata: [],
            columnMetadataStatus: ApiCallStatus.NoData,
            columnMetadataError: "",

            tableData: [{"dummy_column": "dummy_data"}],
            tableDataStatus: ApiCallStatus.NoData,
            tableDataError: "",
        }
    }

    setTableDefinition() {
        // Query data catalog to get table description
        logger.info(`Query data catalog to get table description of ${this.props.tableName}`)
        this.setState(
            {
                tableDefinition: "",
                tableDefinitionStatus: ApiCallStatus.Loading,
                tableDefinitionError: ""
            },
            () => {
                // Will not triggered by user, thus doesn't require UsageTracking
                API.get(
                    dataHubApiConsts.API_NAME,
                    dataHubApiConsts.API_PATH.TABLE_DEFINITION,
                    {
                        queryParams: {
                            tableName: this.props.tableName,
                        },
                    }).then(response => {
                    logger.debug("Response from API")
                    logger.debug(response)
                    this.setState({
                        tableDefinition: response["data"],
                        tableDefinitionStatus: ApiCallStatus.Completed,
                        tableDefinitionError: "",
                    })
                }).catch(err => {
                    logger.error(err)
                    this.setState({
                        tableDefinition: "",
                        tableDefinitionStatus: ApiCallStatus.Error,
                        tableDefinitionError: err+""
                    })
                }).finally(() => {

                })
            }
        )
    }

    setColumnDefinition(callback: () => void) {
        logger.info(`Query data catalog to get column definition of ${this.props.tableName}`)
        this.setState(
            {
                columnMetadata: [],
                columnMetadataStatus: ApiCallStatus.Loading,
                columnMetadataError: ""
            },
            () => {
                let columnOrder: string[] = []
                API.get(
                    dataHubApiConsts.API_NAME,
                    dataHubApiConsts.API_PATH.COLUM_ORDER,
                    {
                        queryParams: {
                            tableName: this.props.tableName
                        },
                    })
                    .then(response => {
                        logger.info("Response from column-order API")
                        logger.info(response)
                        columnOrder = response["data"]
                    })
                    .catch(err => {
                        logger.error(err)
                        this.setState({
                            columnMetadata: [],
                            columnMetadataStatus: ApiCallStatus.Error,
                            columnMetadataError: err+""
                        })
                    }).finally(() => {
                        // Will not triggered by user, thus doesn't require UsageTracking
                        if (columnOrder.length === 0) {
                            return
                        }
                        API.get(
                            dataHubApiConsts.API_NAME,
                            dataHubApiConsts.API_PATH.COLUMN_METADATA,
                            {
                                queryParams: {
                                    tableName: this.props.tableName
                                },
                            })
                            .then(response => {
                                logger.info("Response from API", dataHubApiConsts.API_PATH.COLUMN_METADATA)
                                logger.info(response)

                                // Reorder column metadata according to output of column-order API
                                let columnMetadataArr: ColumnMetadata[] = []
                                for (const columnName of columnOrder) {
                                    for (const columnMetadata of response["data"]) {
                                        if (columnMetadata.name === columnName) {
                                            columnMetadataArr.push(columnMetadata)
                                        }
                                    }
                                }
                                this.setState({
                                    columnMetadata: columnMetadataArr,
                                    columnMetadataStatus: ApiCallStatus.Completed,
                                    columnMetadataError: "",
                                }, callback)
                            })
                            .catch(err => {
                                logger.error(err)
                                this.setState({
                                    columnMetadata: [],
                                    columnMetadataStatus: ApiCallStatus.Error,
                                    columnMetadataError: err+""
                                })
                            }).finally(() => {

                        })
                    })
            }
        )
    }

    searchTableData() {
        if (Object.keys(this.state.columnMetadata).length === 0) {
            return this.setState({
                tableDataStatus: ApiCallStatus.Error,
                tableData: [],
                tableDataError: "There is no column metadata"
            })
        }

        if (this.props.keywords.trim() === "") {
            return this.setState({
                tableDataStatus: ApiCallStatus.Error,
                tableData: [],
                tableDataError: "Keywords must be entered"
            })
        }

        // UsageTracking already added at Search.tsx

        logger.info(`Query data catalog to get table data of ${this.props.tableName} with keyword ${this.props.keywords}`)
        logger.info("comparator ", this.props.comparator)
        this.setState({
            tableDataStatus: ApiCallStatus.Loading,
            tableData: [],
            tableDataError: ""
        }, () => {
            // No UsageTracking here, because we recorded it at componentDidUpdate
            if (this.props.largeTable) {
                // Excluded "remove" columns, since it not presented in the table, 
                // but we still have to search inside hidden columns, 
                // since user may enable their display via toggle bar later, and expect data to be there.
                API.get(
                    dataHubApiConsts.API_NAME,
                    dataHubApiConsts.API_PATH.LARGE_TABLE_DATA,
                    {
                        queryParams: {
                            keywordsCommaSeperated: this.props.keywords,
                            tableName: this.props.tableName,
                            columnCommaSeperated: this.state.columnMetadata.filter(
                                (column) => column.defaultDisplay !== "remove").map((column) => column.name).join(","),
                            displayColumnCommaSeperated: this.state.columnMetadata.map((column) => column.name).join(","), // Will be handled inside SearchResultTableViewOnly, a bit messy arrangement.
                            comparator: this.props.comparator,
                        },
                    })
                    .then(response => {
                        logger.debug("Response from API")
                        logger.debug(response)
                        Storage.get(
                            response["data"].s3Key,
                            {
                                bucket: DATA_HUB_DATA_API_BUCKET_NAME,
                                download: true,
                                contentType: "application/json"
                            })
                            .then(response => {
                                // @ts-ignore
                                if (response.Body) {
                                    // @ts-ignore
                                    response.Body.text().then((str: any) => {
                                        logger.info("searchTableData str = ", str)
                                        this.setState({
                                            tableDataStatus: ApiCallStatus.Completed,
                                            tableData: JSON.parse(str),
                                            tableDataError: "",
                                        })
                                    })
                                } else {
                                    this.setState({
                                        tableDataStatus: ApiCallStatus.Error,
                                        tableData: [],
                                        tableDataError: "Cannot read download blob for some reason"
                                    })
                                }
                            }).catch(error => {
                                logger.error(error)
                                this.setState({
                                    tableDataStatus: ApiCallStatus.Error,
                                    tableData: [],
                                    tableDataError: error+""
                                })
                            })
                    }).catch(err => {
                        logger.error(err)
                        if (err.response.statusCode === 504 || err.response.statusCode === 500) {
                            // Unlike other error code, 504 actually didn't indicate any problem at backend, it's just DB need to warm-up
                            this.setState({
                                tableDataStatus: ApiCallStatus.Error,
                                tableData: [],
                                tableDataError: "Database need to warm-up, please retry the search."
                            })
                        } else {
                            // Need to JSON parse err.response.body 
                            // {"data":null,"message":"cannot find any guide with sequence = AATTAATTAAAATTAATTAA","requestId":"c5ee5662-f9c2-4837-9c9f-0cfb4bf14ea7"} 
                            // to object before getting message
                            const body = JSON.parse(err.response.body)
                            this.setState({
                                tableDataStatus: ApiCallStatus.Error,
                                tableData: [],
                                tableDataError: body.message
                            })
                        }
                    }).finally(() => {

                    })
            } else {
                // FIXME - excluded hidden columns
                API.get(
                    dataHubApiConsts.API_NAME,
                    dataHubApiConsts.API_PATH.TABLE_DATA,
                    {
                        queryParams: {
                            keywordsCommaSeperated: this.props.keywords,
                            tableName: this.props.tableName,
                            columnCommaSeperated: this.state.columnMetadata.filter(
                                (column) => column.defaultDisplay !== "remove").map((column) => column.name).join(","),
                            displayColumnCommaSeperated: this.state.columnMetadata.map((column) => column.name).join(","),
                            comparator: this.props.comparator,
                        },
                    })
                    .then(response => {
                        logger.debug("Response from API")
                        logger.debug(response)
                        this.setState({
                            tableData: response["data"],
                            tableDataStatus: ApiCallStatus.Completed,
                            tableDataError: "",
                        })
                    }).catch(err => {
                        logger.error(err)
                        if (err.response.statusCode === 504 || err.response.statusCode === 500) {
                            // Unlike other error code, 504 actually didn't indicate any problem at backend, it's just DB need to warm-up
                            this.setState({
                                tableDataStatus: ApiCallStatus.Error,
                                tableData: [],
                                tableDataError: "Database need to warm-up, please retry the search."
                            })
                        } else {
                            // Need to JSON parse err.response.body 
                            // {"data":null,"message":"cannot find any guide with sequence = AATTAATTAAAATTAATTAA","requestId":"c5ee5662-f9c2-4837-9c9f-0cfb4bf14ea7"} 
                            // to object before getting message
                            const body = JSON.parse(err.response.body)
                            this.setState({
                                tableDataStatus: ApiCallStatus.Error,
                                tableData: [],
                                tableDataError: body.message
                            })
                        }
                    }).finally(() => {

                    })
            }
        })
    }

    componentDidMount() {
        this.setTableDefinition()
        this.setColumnDefinition(this.searchTableData)
    }

    componentDidUpdate(prevProps: Readonly<DataHubSearchResultTableProps>, prevState: Readonly<DataHubSearchResultTableState>, snapshot?: any) {
        if (prevProps.refreshToken !== this.props.refreshToken) {
            // UsageTracking already place at ../Search.tsx
            this.setTableDefinition()
            this.setColumnDefinition(this.searchTableData)
        }
    }

    render() {
        if (this.state.columnMetadataStatus === ApiCallStatus.NoData
            || this.state.tableDefinitionStatus === ApiCallStatus.NoData
            || this.state.tableDataStatus === ApiCallStatus.NoData) {
            return <></>
        }

        return <SearchResultTableViewOnly
            refreshToken={this.props.refreshToken}
            title={this.props.tableTitle}
            legends={<></>}
            rowClasses={() => ""}
            dataAdmin={""}
            tableName={this.props.tableName}

            tableDescription={this.state.tableDefinition}
            tableDefinitionStatus={this.state.tableDefinitionStatus}
            tableDefinitionErrorMessage={this.state.tableDefinitionError}

            columnMetadata={this.state.columnMetadata}
            columnMetadataStatus={this.state.columnMetadataStatus}
            columnMetadataErrorMessage={this.state.columnMetadataError}

            tableData={this.state.tableData}
            tableDataStatus={this.state.tableDataStatus}
            tableDataErrorMessage={this.state.tableDataError}

            enableColumnFilter={true}
            enableSorting={true}
            displayPagination={true}
        />;
    }
}